/**
 * Toast Notification Service
 *
 * Centralized toast notifications using react-hot-toast
 * Provides consistent notification styling across the application
 *
 * Usage:
 *   import { toast } from '@/lib/toast'
 *   toast.success('Operacion exitosa')
 *   toast.error('Ha ocurrido un error')
 *   toast.loading('Cargando...')
 *   toast.promise(asyncFn(), { loading, success, error })
 */

import hotToast, { Toaster as HotToaster } from 'react-hot-toast'

// =============================================================================
// Types
// =============================================================================

interface ToastOptions {
  /** Duration in milliseconds (default: 4000) */
  duration?: number
  /** Unique ID to prevent duplicates */
  id?: string
  /** Icon to display */
  icon?: string
  /** Position */
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'
}

interface PromiseOptions<T> {
  loading: string
  success: string | ((data: T) => string)
  error: string | ((error: Error) => string)
}

// =============================================================================
// Default Configuration
// =============================================================================

const DEFAULT_DURATION = 4000
const ERROR_DURATION = 6000

// =============================================================================
// Toast Functions
// =============================================================================

/**
 * Show a success toast
 */
function success(message: string, options?: ToastOptions) {
  return hotToast.success(message, {
    duration: DEFAULT_DURATION,
    ...options,
  })
}

/**
 * Show an error toast
 */
function error(message: string | React.ReactNode, options?: ToastOptions) {
  return hotToast.error(message as string, {
    duration: ERROR_DURATION,
    ...options,
  })
}

/**
 * Show a loading toast
 * Returns an ID that can be used to dismiss or update the toast
 */
function loading(message: string, options?: ToastOptions) {
  return hotToast.loading(message, {
    ...options,
  })
}

/**
 * Show a custom toast (info/warning style)
 */
function info(message: string, options?: ToastOptions) {
  return hotToast(message, {
    duration: DEFAULT_DURATION,
    icon: 'ℹ️',
    ...options,
  })
}

/**
 * Show a warning toast
 */
function warning(message: string, options?: ToastOptions) {
  return hotToast(message, {
    duration: DEFAULT_DURATION,
    icon: '⚠️',
    ...options,
  })
}

/**
 * Show a promise toast that updates based on promise state
 * Automatically shows loading, success, or error states
 */
function promise<T>(
  promiseOrFn: Promise<T>,
  options: PromiseOptions<T>
) {
  return hotToast.promise(promiseOrFn, {
    loading: options.loading,
    success: options.success,
    error: options.error,
  })
}

/**
 * Dismiss a specific toast by ID
 */
function dismiss(toastId?: string) {
  hotToast.dismiss(toastId)
}

/**
 * Dismiss all toasts
 */
function dismissAll() {
  hotToast.dismiss()
}

/**
 * Show a custom toast
 */
function custom(message: string, options?: ToastOptions) {
  return hotToast(message, options)
}

// =============================================================================
// Export
// =============================================================================

export const toast = {
  success,
  error,
  info,
  warning,
  loading,
  promise,
  dismiss,
  dismissAll,
  custom,
}

// Re-export Toaster component for App.tsx
export { HotToaster as Toaster }
